import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { formatNumber, Icon, Loader, Theme } from '@tillersystems/stardust';

import { ConvertedRawTicket } from '../../../../modules/OrderOMS/types';
import { ACCEPTED, CASH, WAITING } from '../helpers';

import {
  Cell,
  CellTax,
  Container,
  ContainerWrapper,
  Header,
  Id,
  OrderLines,
  Payments,
  RawTicketSkeleton,
  Row,
  Signature,
  Status,
  StyledDate,
  Table,
  Title,
  Totals,
  Wrapper,
} from './elements';
import PaginationButtons from './PaginationButtons';

const indexedArrayItemTo1 = arrayItem => arrayItem + 1;

type Props = {
  isRawTicketsLoading: boolean;
  onRawTicketSelect: (move: number) => void;
  rawTicket?: ConvertedRawTicket;
  rawTicketsError: Error;
  rawTicketsCount: number;
  rawTicketsIndex: number;
};

const RawTickets: FC<Props> = ({
  isRawTicketsLoading,
  rawTicket,
  rawTicketsIndex,
  rawTicketsCount,
  rawTicketsError,
  onRawTicketSelect,
}) => {
  const [t] = useTranslation();

  const renderLoading = () => (
    <Wrapper>
      <RawTicketSkeleton>
        <Loader width="8rem" height="8rem" />
      </RawTicketSkeleton>
    </Wrapper>
  );

  const renderError = () => (
    <Wrapper>
      <RawTicketSkeleton>
        {t('order_management.raw_tickets.error.no_printed_ticket')}
      </RawTicketSkeleton>
    </Wrapper>
  );

  const renderRawTickets = () => (
    <Wrapper>
      {rawTicketsCount > 1 && (
        <PaginationButtons
          onNext={() => onRawTicketSelect(+1)}
          counter={indexedArrayItemTo1(rawTicketsIndex)}
          maxCount={rawTicketsCount}
          onPrevious={() => onRawTicketSelect(-1)}
        />
      )}
      <ContainerWrapper>
        <Container>
          <Header>
            <Id>{`#${rawTicket.id}`}</Id>
            <StyledDate>
              {DateTime.fromISO(rawTicket.date).toLocaleString(DateTime.DATETIME_SHORT)}
            </StyledDate>
          </Header>
          <OrderLines>
            <Table>
              <tbody>
                {rawTicket.orderLines.map(orderLine => (
                  <Row key={orderLine.uuid} isHighlighted={orderLine.status === WAITING}>
                    <Cell>{orderLine.count}x</Cell>
                    <Cell>
                      <p>{orderLine.name}</p>
                      {orderLine.status === WAITING && <Status>{orderLine.status}</Status>}
                    </Cell>
                    <Cell>
                      {formatNumber({
                        currency: rawTicket.currency,
                        locale: 'fr',
                        number: orderLine.price.tax_included,
                      })}
                    </Cell>
                    <Cell>
                      {formatNumber({
                        currency: rawTicket.currency,
                        locale: 'fr',
                        number: orderLine.total,
                      })}
                    </Cell>
                    <Cell>{orderLine.taxIdentifier}</Cell>
                  </Row>
                ))}
              </tbody>
            </Table>
          </OrderLines>
          <Totals>
            <Table>
              <tbody>
                {rawTicket.taxes.map(tax => (
                  <Row key={tax.key}>
                    <CellTax>
                      {t('order_management.raw_tickets.taxes.vat', {
                        amount: tax.rate,
                        name: tax.identifier,
                      })}
                    </CellTax>
                    <CellTax>
                      {formatNumber({
                        currency: rawTicket.currency,
                        locale: 'fr',
                        number: tax.taxExcluded,
                      })}
                    </CellTax>
                    <CellTax>
                      {formatNumber({
                        currency: rawTicket.currency,
                        locale: 'fr',
                        number: tax.taxAmount,
                      })}
                    </CellTax>
                    <CellTax>
                      {formatNumber({
                        currency: rawTicket.currency,
                        locale: 'fr',
                        number: tax.taxIncluded,
                      })}
                    </CellTax>
                  </Row>
                ))}
                <Row>
                  <CellTax colSpan={3}>
                    {t('order_management.raw_tickets.taxes.excl_tax_total')}
                  </CellTax>
                  <CellTax>
                    {formatNumber({
                      currency: rawTicket.currency,
                      locale: 'fr',
                      number: rawTicket.total.taxExcluded,
                    })}
                  </CellTax>
                </Row>
                <Row>
                  <CellTax colSpan={3}>
                    {t('order_management.raw_tickets.taxes.incl_tax_total')}
                  </CellTax>
                  <CellTax>
                    {formatNumber({
                      currency: rawTicket.currency,
                      locale: 'fr',
                      number: rawTicket.total.taxIncluded,
                    })}
                  </CellTax>
                </Row>
              </tbody>
            </Table>
          </Totals>
          <Payments>
            <Title>{t('order_management.raw_tickets.payments')}</Title>
            <Table>
              <tbody>
                {rawTicket.payments.map(payment => (
                  <Row key={payment.uuid}>
                    <Cell>
                      {payment.status === ACCEPTED ? (
                        <Icon name="check-mark" color={Theme.palette.success.default} size="15px" />
                      ) : (
                        <Icon name="cross" color={Theme.palette.failure.default} size="15px" />
                      )}
                    </Cell>
                    <Cell>
                      {payment.type === CASH
                        ? t('order_management.raw_tickets.cash')
                        : payment.label}
                    </Cell>
                    <Cell>{payment.deviceName}</Cell>
                    <Cell>
                      {formatNumber({
                        currency: rawTicket.currency,
                        locale: 'fr',
                        number: payment.price,
                      })}
                    </Cell>
                  </Row>
                ))}
              </tbody>
            </Table>
          </Payments>
          <Signature>{`Sign. ${rawTicket.signature}`}</Signature>
        </Container>
      </ContainerWrapper>
    </Wrapper>
  );

  return isRawTicketsLoading
    ? renderLoading()
    : rawTicketsError instanceof Error
    ? renderError()
    : rawTicket && renderRawTickets();
};

export default RawTickets;
